import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useOrganization } from '../../contexts/OrganizationContext';

const ProtectedRoute: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const { organization, isLoading } = useOrganization();
  const location = useLocation();

  // Routes that should be accessible even with blocked subscription
  const exemptRoutes = [
    '/subscription/checkout',
    '/subscription/success',
    '/logout',
  ];

  const isExemptRoute = exemptRoutes.some(route => location.pathname.startsWith(route));

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Wait for organization to load before checking status
  if (isLoading) {
    return null; // Or a loading spinner
  }

  // Status that BLOCK access to the system
  const blockedStatuses = ['incomplete', 'past_due', 'canceled', 'suspended'];

  // Check if trial or subscription has expired
  const isTrialExpired = organization &&
    organization.subscriptionStatus === 'trialing' &&
    organization.trialEndsAt &&
    new Date(organization.trialEndsAt) < new Date();

  const isSubscriptionExpired = organization &&
    organization.subscriptionEndsAt &&
    new Date(organization.subscriptionEndsAt) < new Date();

  // Determine if access should be blocked
  const shouldBlockAccess = organization && (
    blockedStatuses.includes(organization.subscriptionStatus) ||
    isTrialExpired ||
    isSubscriptionExpired
  );

  // Redirect to checkout if subscription is not valid
  if (shouldBlockAccess && !isExemptRoute) {
    return (
      <Navigate
        to="/subscription/checkout"
        state={{
          organizationId: organization.id,
          planId: organization.planId,
          userId: user?.id,
          email: user?.email,
          organizationName: organization.name,
          fromSignup: false,
          reason: isTrialExpired ? 'trial_expired' :
                  isSubscriptionExpired ? 'subscription_expired' :
                  organization.subscriptionStatus,
        }}
        replace
      />
    );
  }

  return <Outlet />;
};

export default ProtectedRoute; 